import { featureFlag } from "../constants/feature";
import { isHomeHealthExternalUser } from "../constants/sso";

const INTERNAL_EMAIL_DOMAINS = [
  "@circadia.health",
  "@circadia.com",
  "@circadiatech.com",
] as const;

/**
 * Clinic beta feature for facilities whose visits are done by the facility's
 * own (external) providers rather than Circadia NPs. Opens Navigator to every
 * user at the clinic, regardless of email domain or NP status.
 */
export const NAVIGATOR_EXTERNAL_PROVIDERS_FEATURE =
  "navigator-external-providers";

type NavigatorUser = {
  email?: string | null;
  groups?: string[] | null;
};

export const isInternalCipUser = (user?: NavigatorUser): boolean => {
  if (!user?.email) return false;
  if (isHomeHealthExternalUser(user.groups, user.email)) return false;

  const email = user.email.trim().toLowerCase();

  // PCC SSO customer identities may use synthetic @circadia.health emails.
  if (email.startsWith("pcc_")) return false;
  // Demo accounts are external demonstration customers, not internal staff.
  if (email.includes("demo")) return false;

  return INTERNAL_EMAIL_DOMAINS.some((domain) => email.endsWith(domain));
};

export const hasExternalProviderNavigator = (
  clinicBetaFeatures?: readonly string[] | null
): boolean =>
  clinicBetaFeatures?.includes(NAVIGATOR_EXTERNAL_PROVIDERS_FEATURE) ?? false;

/**
 * Custom to-dos follow Navigator access: anyone who can open Navigator can use
 * them, so granting Navigator is the only switch. Home-health-external /
 * Compassus users use the Compassus Navigator instead.
 */
export const canUseNavigatorCustomTodo = (user?: NavigatorUser): boolean =>
  featureFlag.navigatorCustomTodo &&
  !isHomeHealthExternalUser(user?.groups, user?.email);
